import clsx from "clsx";
import {
  ActionButton,
  B2BSpinner,
  handleUpdateSelectedTravelersList,
  isAdult,
  LoadingIndicator,
} from "halifax";
import React, { useMemo, useState } from "react";
import {
  CorpRoomProduct,
  IPerson,
  ITravelerStepErrors,
  SAVED_NEW_TRAVELER,
  SELECT_TRAVELERS,
  SELECTED_PACKAGE_PRIMARY_TRAVELER,
} from "redmond";

import {
  FlightPassengerSelectors,
  ParentState,
  useCheckoutState,
  useCheckoutStateSelector,
  useChildMachineSelector,
  useChildMachineState,
} from "@capone/checkout";
import { Typography } from "@material-ui/core";
import { trackEvent } from "../../../../../api/v0/analytics/trackEvent";
import { personToIPerson } from "../../../../../utils/personToIPerson";
import { Event, TEvent } from "../../../state/events";
import { LodgingSelectors as PackageLodgingSelectors } from "../../../state/selectors";
import { PackagesMachineContext } from "../../../state/types";
import { DesktopPackageBookValidationError } from "../../DesktopPackagesBookWorkflow";
import { HotelTravelerSelectDropdownForm } from "../HotelTravelerSelectDropdownForm/component";
import { HotelBookPassengerSelectionConnectorProps } from "./container";
import "./styles.scss";
import * as constants from "./textConstants";

export interface IHotelBookPassengerSelectionProps
  extends HotelBookPassengerSelectionConnectorProps {
  chosenProduct: CorpRoomProduct;
  isMobile?: boolean;
  validationErrorTypes?: DesktopPackageBookValidationError[];
  setValidationErrorTypes?: (
    types: DesktopPackageBookValidationError[]
  ) => void;
}

export const HotelBookPassengerSelection = ({
  chosenProduct,
  isMobile,
  validationErrorTypes,
  setValidationErrorTypes,
}: IHotelBookPassengerSelectionProps) => {
  const [, send] = useCheckoutState<TEvent, PackagesMachineContext>();

  const [showErrors, setShowErrors] = useState<ITravelerStepErrors>({
    phone: false,
    email: false,
    travelerSelect: false,
    corpPrimaryTraveler: false,
  });

  const [, passengerChildMachineSend] = useChildMachineState<
    TEvent,
    PackagesMachineContext
  >(ParentState.passengerInformation);

  const travelers = useCheckoutStateSelector(
    PackageLodgingSelectors.getSelectablePassengers
  );
  const selectedTravelerId = useCheckoutStateSelector(
    PackageLodgingSelectors.getSelectedPassengerIdParent
  );

  const selectablePassengers = React.useMemo(
    () =>
      travelers
        ?.filter((person) => isAdult(person.dateOfBirth))
        .map(personToIPerson),
    [travelers]
  );

  const handleSelectPassenger = (passenger?: IPerson) => {
    send({
      type: Event.SELECT_PASSENGER,
      passengerId: passenger?.id,
      singleTravelerWorkflow: true,
    });
  };

  const canContinue = isMobile || !!selectedTravelerId;

  const handleContinue = () => {
    if (canContinue) {
      setValidationErrorTypes?.([]);

      send(Event.NEXT);

      trackEvent({
        eventName: SELECTED_PACKAGE_PRIMARY_TRAVELER,
        properties: {},
      });
    } else {
      setValidationErrorTypes?.(["hotel-travelers"]);
    }
  };

  const handleUpdatePassenger = (passenger: IPerson) => {
    passengerChildMachineSend({
      type: Event.UPDATE_PASSENGER,
      person: {
        ...passenger,
        passport:
          passenger.passport?.countryOfIssue &&
          passenger.passport?.expiration &&
          passenger.passport?.number
            ? passenger.passport
            : undefined,
      },
      currentTravelers: selectablePassengers,
    });
  };

  const canUnselectTraveler = selectablePassengers.length !== 1;

  React.useEffect(() => {
    if (!selectedTravelerId && selectablePassengers.length === 1) {
      handleSelectPassenger(selectablePassengers[0]);
    }
  }, [selectedTravelerId, selectablePassengers]);

  React.useEffect(() => {
    if (canContinue) {
      setValidationErrorTypes?.([]);
    }
  }, [canContinue]);

  const selectedTravelers = useMemo(
    () =>
      selectablePassengers.filter(
        (traveler) => traveler.id === selectedTravelerId
      ),
    [selectedTravelerId, selectablePassengers]
  );

  // all travelers excluding lap infants
  const allSelectedTravelerIds = useCheckoutStateSelector(
    FlightPassengerSelectors.getAllSelectedPassengerIdsParent
  );

  const passengersLoading = useChildMachineSelector(
    ParentState.passengerInformation,
    FlightPassengerSelectors.getIsPassengerInformationLoading
  );

  const handleUpdateSelectedTraveler = (traveler: IPerson) => {
    handleUpdateSelectedTravelersList(
      traveler,
      selectedTravelers,
      (userSelectedTravelersList) => {
        send({
          type: Event.SET_USER_SELECTED_TRAVELERS_LIST,
          userSelectedTravelersList,
        });
      }
    );

    if (!allSelectedTravelerIds.includes(traveler.id)) {
      handleSelectPassenger(traveler);
    }
  };

  return passengersLoading ? (
    <LoadingIndicator
      className="packages-book-passenger-selection-loading-indicator"
      indicatorSize="small"
      indicator={B2BSpinner}
      message={constants.UPDATE_TEXT}
    />
  ) : (
    <>
      <HotelTravelerSelectDropdownForm
        chosenProduct={chosenProduct}
        roomsCount={1} // packages support only one room
        travelers={selectablePassengers}
        disableEditTravelerInfo={false}
        selectedTravelers={selectedTravelers}
        showRemove={canUnselectTraveler}
        updateUserPassenger={(traveler, isNewTraveler) => {
          if (isNewTraveler) {
            trackEvent({
              eventName: SAVED_NEW_TRAVELER,
              properties: {
                funnel: "packages",
              },
            });
          }
          handleUpdatePassenger(traveler);
        }}
        updateSelectedTravelersList={(traveler) => {
          trackEvent({
            eventName: SELECT_TRAVELERS,
            properties: {},
          });
          handleUpdateSelectedTraveler(traveler);
        }}
        loading={passengersLoading}
        removeSelectedTraveler={() => {
          trackEvent({
            eventName: SELECT_TRAVELERS,
            properties: {},
          });
          handleSelectPassenger(undefined);
        }}
        setProgress={() => {}}
        showErrors={showErrors}
        setShowErrors={setShowErrors}
      />
      {!isMobile && (
        <>
          <Typography
            className={clsx("packages-hotel-pax-select-notice", {
              error: validationErrorTypes?.includes("hotel-travelers"),
            })}
          >
            {constants.HOTEL_TRAVELER_NOTICE}
          </Typography>
          <ActionButton
            onClick={handleContinue}
            message={constants.CONTINUE_TEXT}
            className="packages-continue-button"
          />
        </>
      )}
    </>
  );
};
